<svelte:options
  customElement={{
    tag: 'vakken-table',
    shadow: 'none',
    props: {
      inschrijfUrl: { reflect: true, attribute: 'inschrijf-url' },
      sourceUrl: { reflect: true, attribute: 'source-url' },
      disableCollapse: { reflect: true, attribute: 'disable-collapse' },
      showLegend: { reflect: true, attribute: 'show-legend' },
      minVisible: { reflect: true, attribute: 'min-visible' },
    },
  }}
/>

<script lang="ts">
  import { onMount } from 'svelte';
  import { type MouseEventHandler } from 'svelte/elements';

  import CursusPopOver from './CursusPopOver.svelte';
  import expandIcon from './images/expand.svg';

  import {
    type CursusStatus,
    type CursusVak,
    type Periode,
    type VakNiveau,
    type VakkenOverzicht,
  } from './types/vakkenoverzicht';
  import { afgelopen, capitalize, formatDate, getCursus } from './utils';

  type PeriodeWithVisibility = Periode & { visible: boolean };

  // Constants
  const history = 2; // number of 'afgelopen' periodes to show
  const STATUS_INSCHRIJFKNOP: Array<CursusStatus> = ['Open', 'Wachtlijst']; // List of status to show inschrijfknop on

  interface Props {
    niveau: VakNiveau;
    vakken?: Array<CursusVak['code']>;
    disableCollapse?: boolean;
    sourceUrl: string;
    inschrijfUrl: string;
    showLegend?: boolean;
    minVisible?: number;
  }
  const {
    niveau,
    vakken = [],
    disableCollapse = false,
    sourceUrl,
    inschrijfUrl,
    showLegend = true,
    minVisible = 8,
  }: Props = $props();

  let data: VakkenOverzicht = $state({
    cursussen: {
      havo: [],
      vwo: [],
    },
    periodes: [],
  });
  let loaded = $state(false);
  const loadData = async (signal: AbortSignal) => {
    const response = await fetch(sourceUrl, {
      headers: {
        Accept: 'application/json',
      },
      signal,
    });
    data = await response.json() as VakkenOverzicht;
    loaded = true;
  };

  const cursusVakken = $derived(data.cursussen[niveau]);
  const aantalPeriodes = $derived(data.periodes.length);

  let collapsed = $state(true);
  const toggleCollapse = () => {
    collapsed = !collapsed;
  };
  const collapseActive = $derived(!disableCollapse && collapsed);

  // Determines from what index on the periodes are visible
  const visibleFrom = $derived(Math.max(data.periodes.findIndex((periode) => !afgelopen(periode)) - history, 0));
  const canBeExpanded = $derived(collapseActive && visibleFrom > 0 && aantalPeriodes > minVisible);

  // We hide afgelopen periodes, while making sure the last 'history' amount stay visible, and a minimum amount of 'minVisible' is visible
  const getPeriodeVisibility = (index: number, aantalPeriodes: number) =>
    (aantalPeriodes - index) <= minVisible || index >= visibleFrom;
  const periodesWithVisibility: Array<PeriodeWithVisibility> = $derived(data.periodes
    .map((periode, index) => ({
      ...periode,
      visible: getPeriodeVisibility(index, aantalPeriodes),
    })));
  const hidePeriode = (periode: PeriodeWithVisibility) => collapseActive && !periode.visible;

  const cursusInschrijfUrl = (periode: Periode, vak: CursusVak) => {
    const cursus = getCursus(periode, vak);
    if (!inschrijfUrl || !cursus?.status || !STATUS_INSCHRIJFKNOP.includes(cursus.status)) {
      return '';
    }

    const url = new URL(inschrijfUrl);
    url.searchParams.set('Vak_ID', String(vak.Vak_ID));
    url.searchParams.set('Periode_ID', String(periode.Periode_ID));

    return url.toString();
  };

  const clickExpandColumn = () => {
    toggleCollapse();
  };
  const clickExpandButton: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    toggleCollapse();
  };

  onMount(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;
    void loadData(signal);

    return () => {
      abortController.abort();
    };
  });
</script>

{#if showLegend}
<div class="legenda">
  <ul>
    <li>
      <span class="vakopen">&nbsp;</span>
      Open
    </li>
    <li>
      <span class="vakwachtlijst">&nbsp;</span>
      <a href="/FAQ#inschrijvenpraktischwachtlijst" target="_blank">Wachtlijst</a>
    </li>
    <li>
      <span class="vakgesloten">&nbsp;</span>
      Gesloten
    </li>
    <li>
      <span class="vaknietgegeven">&nbsp;</span>
      Niet aangeboden
    </li>
  </ul>
</div>
{/if}
<div class="table-wrapper center">
  <table class="vakkenoverzicht table" cellspacing="0" cellpadding="5" border="1">
    {#if loaded}
    <tbody>
      <tr class="periodes">
        <th class="periode">Periode</th>
        {#if canBeExpanded}
          <th
            class="expander"
            title="Klik om uit te klappen."
            rowspan="{data.cursussen[niveau].length + 2}"
            onclick={clickExpandColumn}
          >
            <button class="expander-btn" onclickcapture={clickExpandButton}>
              <img
                alt="expand"
                class="expand-icon"
                height="20"
                width="20"
                src={expandIcon}
              />
            </button>
          </th>
        {/if}
        {#each periodesWithVisibility as periode (periode.Periode_ID)}
          <th class="periode" class:hide={hidePeriode(periode)}>{periode.naam}</th>
        {/each}
      </tr>
      <tr class="data">
        <th class="datatitel">Data {niveau}</th>
        {#each periodesWithVisibility as periode (periode.Periode_ID)}
          <th class="periodedata" class:hide={hidePeriode(periode)}>
            <ul class="data-periode-list">
              {#each periode.dagen as dag (dag)}
                <li>{ formatDate(dag, { weekday: false, month: true }) }</li>
              {/each}
            </ul>
          </th>
        {/each}
      </tr>

      {#each cursusVakken as vak (vak.Vak_ID)}
        {#if vakken.length === 0 || (vakken.length > 0 && vakken.includes(vak.code))}
          <tr class="vak">
            <td class="vaknaam">{capitalize(vak.naam)}</td>
            {#each periodesWithVisibility as periode (periode.Periode_ID)}
              {@const cursus = getCursus(periode, vak)}
              <td
                class:cursus={true}
                class={cursus ? `vak${cursus.status.toLowerCase()}` : 'vaknietgegeven'}
                class:hide={hidePeriode(periode)}
              >
                {#if cursus}
                <CursusPopOver
                  periode={periode}
                  vak={vak}
                  inschrijfUrl={cursusInschrijfUrl(periode, vak)}
                />
                {/if}
              </td>
            {/each}
          </tr>
        {/if}
      {/each}
    </tbody>
    {/if}
  </table>
</div>

<style lang="scss">
  @use "./scss/variables";

  .legenda {
    margin: 1rem;
    float: left;

    ul {
      list-style: none;
      padding-left: 0;

      span {
        width: 1.5rem;
        display: inline-block;
      }
    }
  }

  .hide {
    display: none;
  }

  .table.vakkenoverzicht {
    border: 0;
    border-collapse: separate;
    font-family: variables.$font-family-serif;
    height: 100%; /* To support height:100% inside td */
    margin-bottom: 0;
    table-layout: fixed;
  }

  th {
    border: 1px;
    text-align: center;

    &.datatitel {
      color: variables.$ssl-blue;
      text-align: left;
      vertical-align: middle;
    }

    &.periode {
      background-color: variables.$ssl-blue;
      color: variables.$white;
    }

    &.periodedata {
      font-weight: variables.$font-weight-normal;
      font-size: 0.8rem;
      white-space: nowrap;
    }
  }

  tr {
    &.data {
      background-color: variables.$ssl-blue-light !important;
      font-weight: variables.$font-weight-bold;
      text-align: center;
    }

    &.periodes {
      font-size: 1.2em;
      font-weight: variables.$font-weight-bold;
    }

    &.vak {
      height: 30px;

      &:nth-of-type(2n+1) {
        background-color: variables.$white;
      }
    }
  }

  td {
    height: 100%; /* To support height:100% inside td */
    text-align: center;

    &.cursus {
      height: 100%;
    }

    &.vaknaam {
      text-align: left;
    }

    &.vakwachtlijst,
    &.vakopen,
    &.vakgesloten {
      padding: 0;
    }

    &.vakwachtlijst,
    &.vakopen {
      &:hover {
        cursor: pointer;
      }
    }
  }

  td:first-child,
  th:first-child {
    font-weight: variables.$font-weight-bold;
    left: 0;
    position: sticky;
    z-index: 1;
  }

  td:first-child,
  th:first-child:not(.periode) {
    background-color: inherit;
  }

  th.expander {
    background-color: rgb(151, 151, 151);
    border: 0;
    box-shadow: inset 10px 0 12px -11px #000;
    padding: 0.2rem;
    text-align: center;
    transition: all 0.5s;
    vertical-align: middle;
    width: 40px;

    &:hover {
      background-color: rgb(123, 123, 123);
      width: 50px;
    }

    .expander-btn {
      border: 0;
      border-radius: 20px;
      cursor: pointer;
      height: 32px;
      line-height: 0;
      padding: 0;
      text-align: center;
      width: 32px;
    }
  }

  .periodedata {
    padding: 0.25rem 0.4rem;

    .data-periode-list {
      display: block;
      min-width: 7ch;
      list-style: none;
      margin: 0;
      padding: 0;
    }
  }

  .vakopen {
    background-color: variables.$color-success;
  }

  .vakwachtlijst {
    background-color: variables.$color-warning;
  }

  .vakgesloten {
    background-color: variables.$color-danger;
  }

  .vaknietgegeven {
    background-color: variables.$color-dark;
  }
</style>
